import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Empty, Select, Table, Tag, Typography } from 'antd';

import {
  fetchAssessmentResultDetail,
  selectors,
  AssessmentPillar4Detail,
  AssessmentPillar4Transition,
  AssessmentPillar4TransitionRef,
  AssessmentPillarPeriod,
  AssessmentPillarPeriods,
  AssessmentResultRow,
} from 'state';
import { formatters } from 'services';
import type { TableColumnsType as ColumnsType } from 'antd';

import Explainer, { EXPLAINERS } from './explainers';

type PillarKey = '1' | '2' | '3' | '4' | '5';

// slider_revision_fit -> "Slider revision fit"
const humanize = (key: string) => {
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const num1 = (value: unknown) =>
  typeof value === 'number' ? formatters.commasExact1Digit(value) : '-';

const transitionLabel = (ref: AssessmentPillar4TransitionRef | undefined) => {
  if (ref === null || ref === undefined) return '-';
  if (typeof ref === 'string') return ref;
  const span =
    ref.from_period !== undefined && ref.to_period !== undefined
      ? `P${ref.from_period} → P${ref.to_period}`
      : '';
  const label = [span, ref.instrument_parent, ref.event_type]
    .filter(Boolean)
    .join(' · ');
  return label || '-';
};

const StatCard: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Card size="small" className="assessment-report-card">
    <div className="assessment-report-card-label">{label}</div>
    <div className="assessment-report-card-value">{value}</div>
  </Card>
);

// Engine bookkeeping carried inside `components` — never a metric.
// Mirrors pm.api services/metric_glossary.py IDENTITY_KEYS. `pillar_N_score`
// is dropped too: the table already ends with a dedicated Score column, so
// carrying it as a component printed the same number twice.
const IDENTITY_KEYS = ['candidate', 'name', 'label', 'period'];
const isPillarScore = (key: string) => /^pillar_\d+_score$/.test(key);

// Reading order per pillar: counts first (what happened), then the ratios
// derived from them, then the remaining scored components. Anything not
// listed keeps its natural order at the end, so a new engine component
// appears rather than silently vanishing.
const COMPONENT_ORDER: { [pillar: string]: string[] } = {
  '1': [
    'assigned_pairs',
    'tp_pairs',
    'fp_pairs',
    'fn_pairs',
    'mapping_f1',
    'mapping_recall',
    'mapping_precision',
  ],
};

const orderComponents = (pillar: string, keys: string[]) => {
  const preferred = COMPONENT_ORDER[pillar] || [];
  const ranked = preferred.filter(key => keys.includes(key));
  const rest = keys.filter(key => !preferred.includes(key));
  return [...ranked, ...rest];
};

/** Period-by-period component table for pillars 1, 2, 3 and 5. */
const PeriodTable: React.FC<{
  periods: AssessmentPillarPeriod[];
  pillar: string;
}> = ({ periods, pillar }) => {
  const componentKeys = useMemo(() => {
    const keys: string[] = [];
    periods.forEach(period =>
      Object.keys(period.components || {}).forEach(key => {
        // pm.api stores asdict(period_score), so the engine's identity fields
        // ride along with the real components. They say WHOSE score this is,
        // not what was measured — rendering them produced a '1.0' Period
        // column and three empty ones (Name / Label / Candidate).
        if (IDENTITY_KEYS.includes(key) || isPillarScore(key)) return;
        if (!keys.includes(key)) keys.push(key);
      }),
    );
    return orderComponents(pillar, keys);
  }, [periods, pillar]);

  const columns: ColumnsType<AssessmentPillarPeriod> = useMemo(
    () => [
      {
        title: 'Period',
        dataIndex: 'label',
        key: 'label',
        render: (label: string, row) => label || `P${row.period}`,
      },
      {
        title: 'Regime',
        dataIndex: 'regime',
        key: 'regime',
        render: (regime: string) => (regime ? <Tag>{regime}</Tag> : '-'),
      },
      ...componentKeys.map(key => ({
        title: humanize(key),
        key,
        className: 'assessment-report-num',
        render: (_: unknown, row: AssessmentPillarPeriod) =>
          num1(row.components?.[key]),
      })),
      {
        title: 'Score',
        dataIndex: 'score',
        key: 'score',
        className: 'assessment-report-num assessment-report-score-cell',
        render: num1,
      },
    ],
    [componentKeys],
  );

  return (
    <Table
      dataSource={periods}
      columns={columns}
      rowKey="period"
      bordered
      size="small"
      pagination={false}
    />
  );
};

// Card order for the pillar 4 component summary row (status rendered apart).
const P4_COMPONENT_ORDER: {
  key: keyof AssessmentPillar4Detail['components'];
  label: string;
}[] = [
  { key: 'pillar_4_provisional', label: 'Pillar score (provisional)' },
  { key: 'slider_revision_fit', label: 'Slider revision fit' },
  { key: 'position_revision_fit', label: 'Position revision fit' },
  { key: 'invalidation_response', label: 'Invalidation response' },
  { key: 'no_action_proxy', label: 'No-action proxy' },
  { key: 'evidence_coverage', label: 'Evidence coverage' },
];

/** Pillar 4 (Updating) — component cards + diagnostics + transitions table. */
const Pillar4Panel: React.FC<{ detail: AssessmentPillar4Detail }> = ({
  detail,
}) => {
  const { components, diagnostics, transitions } = detail;

  const columns: ColumnsType<AssessmentPillar4Transition> = useMemo(
    () => [
      {
        title: 'From → To',
        key: 'span',
        className: 'assessment-report-num',
        render: (_, row) => `P${row.from_period} → P${row.to_period}`,
      },
      {
        title: 'Parent',
        dataIndex: 'instrument_parent',
        key: 'instrument_parent',
      },
      {
        title: 'Event',
        dataIndex: 'event_type',
        key: 'event_type',
        render: (event: string) => (event ? <Tag>{event}</Tag> : '-'),
      },
      {
        title: 'Slider revision fit',
        dataIndex: 'slider_revision_fit',
        key: 'slider_revision_fit',
        className: 'assessment-report-num',
        render: num1,
      },
      {
        title: 'Position revision fit',
        dataIndex: 'position_revision_fit',
        key: 'position_revision_fit',
        className: 'assessment-report-num',
        render: num1,
      },
      {
        title: 'Invalidation response',
        dataIndex: 'invalidation_response',
        key: 'invalidation_response',
        className: 'assessment-report-num',
        render: num1,
      },
      {
        title: 'No-action proxy',
        dataIndex: 'no_action_proxy',
        key: 'no_action_proxy',
        className: 'assessment-report-num',
        render: num1,
      },
    ],
    [],
  );

  return (
    <div className="assessment-report-p4">
      <div className="assessment-report-cards">
        {P4_COMPONENT_ORDER.map(({ key, label }) => (
          <StatCard key={key} label={label} value={num1(components?.[key])} />
        ))}
        <StatCard
          label="Status"
          value={
            components?.status ? (
              <Tag color="blue">
                Provisional: scored from observed behaviour only (stated
                reasoning is not yet captured)
              </Tag>
            ) : (
              '-'
            )
          }
        />
      </div>
      {diagnostics && (
        <div className="assessment-report-cards">
          <StatCard
            label="Hard invalidation"
            value={`${diagnostics.strong_hard_responses ?? 0} / ${
              diagnostics.hard_events ?? 0
            } strong`}
          />
          <StatCard
            label="Thesis decay"
            value={`${diagnostics.strong_decay_responses ?? 0} / ${
              diagnostics.decay_events ?? 0
            } strong`}
          />
          <StatCard
            label="No-action"
            value={`${diagnostics.strong_no_action_responses ?? 0} / ${
              diagnostics.no_action_events ?? 0
            } strong`}
          />
          <StatCard
            label="Best transition"
            value={transitionLabel(diagnostics.best_transition)}
          />
          <StatCard
            label="Weakest transition"
            value={transitionLabel(diagnostics.weakest_transition)}
          />
        </div>
      )}
      <Table
        dataSource={transitions || []}
        columns={columns}
        rowKey={(row, index) =>
          `${row.from_period}-${row.to_period}-${row.instrument_parent}-${index}`
        }
        bordered
        size="small"
        pagination={false}
      />
    </div>
  );
};

type Props = {
  pillar: PillarKey;
  players: AssessmentResultRow[];
  player?: number;
  onPlayerChange: (player: number) => void;
};

/** One pillar tab of the trainer report: player picker, explainer, breakdown. */
const PillarTab: React.FC<Props> = ({
  pillar,
  players,
  player,
  onPlayerChange,
}) => {
  const dispatch = useDispatch();
  const resultDetails = useSelector(selectors.assessmentResultDetails);

  const detail = player !== undefined ? resultDetails.items[player] : undefined;
  const fetching =
    player !== undefined && resultDetails.fetching[player] === true;

  // Lazy fetch, once per player: `fetching[player] === false` means a previous
  // attempt failed — don't loop (Refresh on the Summary tab clears markers).
  useEffect(() => {
    if (player === undefined || detail) return;
    if (resultDetails.fetching[player] !== undefined) return;
    dispatch(fetchAssessmentResultDetail({ player }));
  }, [dispatch, player, detail, resultDetails.fetching]);

  const pillarData = detail?.pillars?.[pillar];

  let content: React.ReactNode;
  if (player === undefined) {
    content = (
      <Empty
        description={
          <Typography.Text type="secondary">
            Select a player to see their breakdown.
          </Typography.Text>
        }
      />
    );
  } else if (!detail && fetching) {
    content = <Table dataSource={[]} columns={[]} loading pagination={false} />;
  } else if (!pillarData) {
    content = (
      <Empty
        description={
          <Typography.Text type="secondary">
            No breakdown available for this player yet — recompute the
            assessment once the session has finished.
          </Typography.Text>
        }
      />
    );
  } else if (pillar === '4') {
    content = <Pillar4Panel detail={pillarData as AssessmentPillar4Detail} />;
  } else {
    content = (
      <PeriodTable
        pillar={pillar}
        periods={(pillarData as AssessmentPillarPeriods).periods || []}
      />
    );
  }

  return (
    <div className="assessment-report-tab">
      <div className="assessment-report-player-row">
        <Typography.Text type="secondary">Player</Typography.Text>
        <Select
          style={{ width: 260 }}
          placeholder="Select a player"
          value={player}
          onChange={onPlayerChange}
          options={players.map(row => ({
            value: row.player,
            label: row.player_name,
          }))}
        />
      </div>
      <Explainer copy={EXPLAINERS[pillar]} />
      {content}
    </div>
  );
};

export default PillarTab;
